<template>
  <article :class="['article-detail', { 'article-detail--reading': isReadingMode }]">
    <LoadingSpinner v-if="loading" text="正在加载文章..." />

    <section v-else-if="errorMessage" class="article-error" role="alert">
      <h1>文章加载失败</h1>
      <p>{{ errorMessage }}</p>
      <el-button type="primary" @click="fetchArticle">重新加载</el-button>
    </section>

    <template v-else>
      <div v-show="isReadingMode" class="reading-progress" aria-hidden="true">
        <span :style="{ transform: `scaleX(${readingProgress / 100})` }"></span>
      </div>

      <button
        ref="readingModeButton"
        type="button"
        class="reading-mode-toggle"
        :class="{ 'reading-mode-toggle--active': isReadingMode }"
        :disabled="mermaidRendering"
        :aria-pressed="isReadingMode ? 'true' : 'false'"
        :aria-busy="mermaidRendering ? 'true' : 'false'"
        :aria-label="isReadingMode ? '退出沉浸阅读' : '进入沉浸阅读'"
        @click="toggleReadingMode">
        <i :class="isReadingMode ? 'el-icon-close' : 'el-icon-full-screen'" aria-hidden="true"></i>
        <span>{{ mermaidRendering ? '准备图表…' : (isReadingMode ? '退出阅读' : '沉浸阅读') }}</span>
        <kbd v-if="isReadingMode" class="reading-mode-shortcut">Esc</kbd>
      </button>

      <header class="article-header">
      <div class="article-meta">
        <div class="meta-row">
          <span class="article-category">
            {{ article.Category ? article.Category.name : '未分类' }}
          </span>
          <div class="date-info">
            <time class="article-update-date">
              <i class="el-icon-refresh"></i>
              更新于 {{ formatDate(article.UpdatedAt || article.CreatedAt) }}
            </time>
          </div>
        </div>
      </div>
      <h1 class="article-title">{{ article.title }}</h1>
      <p class="article-desc">{{ article.desc }}</p>
      </header>

      <div
        ref="markdownContent"
        class="article-content markdown-body"
        v-html="compiledMarkdown"
        @click="handleContentClick"
        @keydown="handleContentKeydown">
      </div>

      <transition name="diagram-viewer">
        <section
          v-if="diagramViewer.open"
          ref="diagramViewer"
          class="diagram-viewer"
          role="dialog"
          aria-modal="true"
          :aria-label="`${diagramViewer.title}放大预览`"
          @click.self="closeDiagramViewer">
          <div class="diagram-viewer__panel">
            <header class="diagram-viewer__header">
              <div>
                <span class="diagram-viewer__eyebrow">{{ diagramViewer.type }}预览</span>
                <h2 class="diagram-viewer__title">{{ diagramViewer.title }}</h2>
              </div>
              <div class="diagram-viewer__controls" aria-label="预览缩放控制">
                <button
                  type="button"
                  class="diagram-viewer__button"
                  :disabled="diagramViewer.scale <= 0.25"
                  title="缩小"
                  aria-label="缩小预览"
                  @click="changeDiagramZoom(-0.25)">
                  <i class="el-icon-minus" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  class="diagram-viewer__button diagram-viewer__scale"
                  title="恢复为适合窗口的大小"
                  aria-label="恢复为适合窗口的大小"
                  @click="resetDiagramView">
                  {{ diagramScaleLabel }}
                </button>
                <button
                  type="button"
                  class="diagram-viewer__button"
                  :disabled="diagramViewer.scale >= 4"
                  title="放大"
                  aria-label="放大预览"
                  @click="changeDiagramZoom(0.25)">
                  <i class="el-icon-plus" aria-hidden="true"></i>
                </button>
                <button
                  ref="diagramCloseButton"
                  type="button"
                  class="diagram-viewer__button diagram-viewer__close"
                  title="关闭"
                  aria-label="关闭放大预览"
                  @click="closeDiagramViewer">
                  <i class="el-icon-close" aria-hidden="true"></i>
                </button>
              </div>
            </header>

            <div
              ref="diagramViewport"
              class="diagram-viewer__viewport"
              :class="{ 'is-dragging': diagramViewer.dragging }"
              aria-label="可缩放、可拖动的预览内容"
              @wheel.prevent="handleDiagramWheel"
              @dblclick="resetDiagramView"
              @pointerdown="startDiagramPan"
              @pointermove="moveDiagramPan"
              @pointerup="endDiagramPan"
              @pointercancel="endDiagramPan">
              <div
                ref="diagramCanvas"
                class="diagram-viewer__canvas"
                :style="diagramTransformStyle">
              </div>
            </div>

            <p class="diagram-viewer__hint">
              滚轮缩放 · 拖动查看 · 双击复位 · Esc 关闭
            </p>
          </div>
        </section>
      </transition>

      <div
        v-if="tocItems.length > 0 && showToc"
        class="toc-backdrop"
        aria-hidden="true"
        @click="toggleToc">
      </div>

      <aside
        v-if="tocItems.length > 0"
        ref="tocContainer"
        class="toc-container"
        v-show="showToc"
        aria-label="文章目录">
        <div class="toc-header">
          <h2 class="toc-title">
          <i class="el-icon-menu"></i>
          目录
          </h2>
          <button ref="tocClose" type="button" @click="toggleToc" class="toc-toggle" aria-label="关闭目录">
            <i class="el-icon-close" aria-hidden="true"></i>
          </button>
        </div>
        <nav class="toc-content" aria-label="文章章节">
          <ul class="toc-list">
            <li
              v-for="item in tocItems"
              :key="item.id"
              :class="['toc-item', `toc-level-${item.level}`]">
              <button
                type="button"
                class="toc-link"
                :class="{ 'is-active': activeHeadingId === item.id }"
                :aria-current="activeHeadingId === item.id ? 'location' : null"
                @click="scrollToHeading(item.id)">
                <span class="toc-text">{{ item.text }}</span>
              </button>
            </li>
          </ul>
        </nav>
      </aside>

      <button
        v-if="tocItems.length > 0 && !showToc"
        ref="tocToggleBtn"
        type="button"
        class="toc-toggle-btn"
        @click="toggleToc"
        aria-label="打开文章目录"
        :aria-expanded="showToc ? 'true' : 'false'">
        <i class="el-icon-menu" aria-hidden="true"></i>
        <span class="toggle-text">目录</span>
      </button>

      <footer class="article-footer">
      <div class="article-tags">
        <span class="tag" v-for="tag in article.tags" :key="tag">{{ tag }}</span>
      </div>
      <div class="action-buttons" v-if="isLogin">
        <el-button
          type="text"
          size="small"
          icon="el-icon-edit"
          @click="$router.push(`/article/edit?id=${article.ID}`)"
          class="action-button">
          编辑
        </el-button>
        <el-button
          type="text"
          size="small"
          icon="el-icon-delete"
          @click="handleDelete"
          class="action-button delete-button">
          删除
        </el-button>
      </div>
      </footer>

      <!-- 评论组件 -->
      <div class="article-comments">
        <Comment v-if="article.ID" :article-id="Number(article.ID)" :is-admin="isLogin" />
      </div>

      <el-button
        v-if="isLogin"
        type="primary"
        icon="el-icon-edit"
        @click="$router.push('/article/edit')"
        class="float-button">
        发表文章
      </el-button>
    </template>
  </article>
</template>

<script>
import { getArticleInfo, deleteArticle } from '@/api'
import Comment from '@/components/Comment'
import LoadingSpinner from '@/components/LoadingSpinner'
import { renderMarkdown, hasMermaidDiagram } from '@/utils/markdown'
import {
  hasPlantUmlDiagram,
  renderPlantUmlDiagrams as hydratePlantUmlDiagrams
} from '@/utils/plantuml'
import 'highlight.js/styles/github.css'
import 'katex/dist/katex.min.css'
import 'markdown-it-texmath/css/texmath.css'

let mermaidInstance = null
let mermaidLoader = null

function getMermaidAssetUrl() {
  const base = String(process.env.BASE_URL || '/').replace(/\/+$/, '')
  return `${base}/vditor/dist/js/mermaid/mermaid.min.js`
}

function loadMermaid() {
  if (mermaidInstance) return Promise.resolve(mermaidInstance)
  if (window.mermaid) {
    mermaidInstance = window.mermaid
    return Promise.resolve(mermaidInstance)
  }
  if (mermaidLoader) return mermaidLoader

  const loader = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = getMermaidAssetUrl()
    script.async = true
    script.dataset.mermaidRuntime = 'true'
    script.onload = () => {
      if (!window.mermaid) {
        reject(new Error('Mermaid 运行时加载失败'))
        return
      }
      mermaidInstance = window.mermaid
      resolve(mermaidInstance)
    }
    script.onerror = () => reject(new Error('Mermaid 资源加载失败'))
    document.head.appendChild(script)
  })

  mermaidLoader = loader.catch(error => {
    mermaidLoader = null
    throw error
  })

  return mermaidLoader
}

export default {
  name: 'ArticleDetail',
  components: {
    Comment,
    LoadingSpinner
  },
  data() {
    return {
      article: {
        title: '',
        desc: '',
        content: '',
        CreatedAt: '',
        Category: null,
        tags: []
      },
      compiledMarkdown: '',
      isLogin: false,
      loading: true,
      errorMessage: '',
      tocItems: [],
      activeHeadingId: '',
      showToc: false,
      headingObserver: null,
      diagramViewer: {
        open: false,
        type: '图表',
        title: '图表',
        scale: 1,
        fitScale: 1,
        offsetX: 0,
        offsetY: 0,
        dragging: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        startOffsetX: 0,
        startOffsetY: 0
      },
      diagramTrigger: null,
      previousBodyOverflow: '',
      mermaidRendering: false,
      mermaidRenderError: '',
      mermaidRenderPromise: null,
      isReadingMode: false,
      readingProgress: 0,
      readingScrollFrame: null
    }
  },
  computed: {
    diagramTransformStyle() {
      return {
        transform: `translate(-50%, -50%) translate3d(${this.diagramViewer.offsetX}px, ${this.diagramViewer.offsetY}px, 0) scale(${this.diagramViewer.scale})`
      }
    },
    diagramScaleLabel() {
      return `${Math.round(this.diagramViewer.scale * 100)}%`
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return '未知时间'

      const parsedDate = new Date(date)
      if (Number.isNaN(parsedDate.getTime())) return '未知时间'

      return parsedDate.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    async fetchArticle() {
      this.closeDiagramViewer(false)
      this.isReadingMode = false
      this.readingProgress = 0
      document.body.classList.remove('article-reading-mode')
      this.loading = true
      this.errorMessage = ''
      this.compiledMarkdown = ''
      this.tocItems = []
      this.activeHeadingId = ''
      this.disconnectHeadingObserver()

      try {
        const id = this.$route.params.id
        const res = await getArticleInfo(id)
        if (!res || !res.data) {
          throw new Error('没有找到这篇文章')
        }

        this.article = res.data
        this.compiledMarkdown = renderMarkdown(this.article.content)
        document.title = `${this.article.title} - x-shuffle`

        const metaDescription = document.querySelector('meta[name="description"]')
        if (metaDescription) {
          metaDescription.setAttribute('content', this.article.desc || 'x-shuffle 博客文章')
        }

        this.loading = false
        await this.$nextTick()
        this.extractToc()
        this.observeHeadings()
        if (hasMermaidDiagram(this.article.content)) {
          await this.renderMermaidDiagrams()
        }
        if (hasPlantUmlDiagram(this.article.content)) {
          this.renderPlantUmlDiagrams()
        }
        this.prepareDiagramPreviewTargets()
      } catch (error) {
        console.error('获取或渲染文章失败:', error)
        this.errorMessage = error.message || '请稍后重试'
      } finally {
        this.loading = false
      }
    },
    checkLoginStatus() {
      this.isLogin = Boolean(localStorage.getItem('token'))
    },
    async handleDelete() {
      try {
        await this.$confirm('确认删除这篇文章吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await deleteArticle(this.article.ID)
        this.$message.success('删除成功')
        this.$router.push('/')
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('删除失败')
        }
      }
    },
    extractToc() {
      const contentElement = this.$refs.markdownContent
      if (!contentElement) return

      const headings = contentElement.querySelectorAll('h1, h2, h3, h4, h5, h6')
      this.tocItems = Array.from(headings).map(heading => ({
        id: heading.id,
        text: heading.textContent.trim(),
        level: Number(heading.tagName.charAt(1))
      })).filter(item => item.id && item.text)

      this.activeHeadingId = this.tocItems.length ? this.tocItems[0].id : ''
    },
    observeHeadings() {
      if (!('IntersectionObserver' in window) || !this.$refs.markdownContent) return

      const headings = this.$refs.markdownContent.querySelectorAll('h1, h2, h3, h4, h5, h6')
      const headerHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        10
      ) || 64
      this.headingObserver = new IntersectionObserver(entries => {
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)

        if (visible.length) this.activeHeadingId = visible[0].target.id
      }, {
        rootMargin: `-${headerHeight + 16}px 0px -68% 0px`,
        threshold: [0, 1]
      })

      headings.forEach(heading => this.headingObserver.observe(heading))
    },
    disconnectHeadingObserver() {
      if (this.headingObserver) {
        this.headingObserver.disconnect()
        this.headingObserver = null
      }
    },
    async renderMermaidDiagrams(options = {}) {
      while (this.mermaidRenderPromise) {
        await this.mermaidRenderPromise
      }

      const container = this.$refs.markdownContent
      if (!container) return false
      const nodes = Array.from(container.querySelectorAll('.mermaid'))
        .filter(node => !node.querySelector('svg'))
      if (!nodes.length) {
        this.prepareDiagramPreviewTargets()
        return true
      }

      const renderTask = this.performMermaidRender(nodes, options)
      this.mermaidRenderPromise = renderTask

      try {
        return await renderTask
      } finally {
        if (this.mermaidRenderPromise === renderTask) {
          this.mermaidRenderPromise = null
        }
      }
    },
    async performMermaidRender(nodes, options = {}) {
      this.mermaidRendering = true
      this.mermaidRenderError = ''
      nodes.forEach(node => {
        const source = node.dataset.mermaidSource || node.textContent || ''
        node.dataset.mermaidSource = source
        node.textContent = source
        node.removeAttribute('data-processed')
        node.classList.remove('is-render-error')
        node.classList.add('is-rendering')

        const block = node.closest('.diagram-block')
        const hint = block ? block.querySelector('.diagram-zoom-hint') : null
        if (hint) hint.textContent = '正在生成…'
      })

      try {
        if (!mermaidInstance) {
          mermaidInstance = await loadMermaid()
          mermaidInstance.initialize({
            startOnLoad: false,
            securityLevel: 'strict',
            theme: 'neutral',
            fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
          })
        }

        await mermaidInstance.run({ nodes })
        const rendered = nodes.every(node => node.querySelector('svg'))
        if (!rendered) throw new Error('Mermaid 未生成 SVG')

        this.prepareDiagramPreviewTargets()
        nodes.forEach(node => {
          const block = node.closest('.diagram-block')
          const hint = block ? block.querySelector('.diagram-zoom-hint') : null
          if (hint) hint.textContent = '点击放大'
        })
        return true
      } catch (error) {
        console.error('Mermaid 图表渲染失败:', error)
        this.mermaidRenderError = error.message || 'Mermaid 图表渲染失败'
        nodes.forEach(node => {
          node.classList.add('is-render-error')
          const block = node.closest('.diagram-block')
          const hint = block ? block.querySelector('.diagram-zoom-hint') : null
          if (hint) hint.textContent = '渲染失败'
        })
        if (options.notifyOnError) {
          this.$message.error('图表渲染失败，请稍后重试')
        }
        return false
      } finally {
        nodes.forEach(node => node.classList.remove('is-rendering'))
        this.mermaidRendering = false
      }
    },
    renderPlantUmlDiagrams() {
      hydratePlantUmlDiagrams(this.$refs.markdownContent)
      this.prepareDiagramPreviewTargets()
    },
    prepareDiagramPreviewTargets() {
      const content = this.$refs.markdownContent
      if (!content) return

      content.querySelectorAll('.mermaid svg').forEach(svg => {
        svg.classList.add('diagram-preview-target')
        svg.dataset.diagramPreview = 'mermaid'
        svg.setAttribute('tabindex', '0')
        svg.setAttribute('role', 'button')
        svg.setAttribute('aria-label', '放大 Mermaid 图表')
      })

      content.querySelectorAll('img:not([data-diagram-preview])').forEach(image => {
        image.classList.add('diagram-preview-target')
        image.dataset.diagramPreview = 'image'
        image.setAttribute('tabindex', '0')
        image.setAttribute('role', 'button')
        image.setAttribute(
          'aria-label',
          image.alt ? `放大图片：${image.alt}` : '放大文章图片'
        )
      })
    },
    async handleContentClick(event) {
      const content = this.$refs.markdownContent
      const target = event.target && event.target.closest ? event.target : null
      if (!content || !target) return

      const button = target.closest('[data-code-copy]')
      if (button && content.contains(button)) {
        await this.copyCodeBlock(button)
        return
      }

      const diagram = target.closest('[data-diagram-preview]')
      if (diagram && content.contains(diagram)) {
        this.openDiagramViewer(diagram)
      }
    },
    handleContentKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return
      const content = this.$refs.markdownContent
      const diagram = event.target && event.target.closest
        ? event.target.closest('[data-diagram-preview]')
        : null
      if (!content || !diagram || !content.contains(diagram)) return

      event.preventDefault()
      this.openDiagramViewer(diagram)
    },
    async copyCodeBlock(button) {
      const codeBlock = button.closest('.code-block')
      const code = codeBlock && codeBlock.querySelector('code')
      if (!code) return

      try {
        await this.copyText(code.textContent)
        button.textContent = '已复制'
        button.classList.add('is-copied')
        window.setTimeout(() => {
          button.textContent = '复制'
          button.classList.remove('is-copied')
        }, 1600)
      } catch (error) {
        console.error('复制代码失败:', error)
        this.$message.error('复制失败，请手动选择代码')
      }
    },
    openDiagramViewer(target) {
      if (!target) return

      const clone = target.cloneNode(true)
      const figure = target.closest('.diagram-block')
      const title = figure && figure.querySelector('.diagram-toolbar span')
      const isArticleImage = target.dataset.diagramPreview === 'image'
      const bounds = target.getBoundingClientRect()
      let naturalWidth = bounds.width

      clone.removeAttribute('tabindex')
      clone.removeAttribute('role')
      clone.removeAttribute('aria-label')
      clone.removeAttribute('data-diagram-preview')
      clone.classList.remove('diagram-preview-target')
      clone.classList.add('diagram-viewer__asset')

      if (target.tagName.toLowerCase() === 'svg' && target.viewBox && target.viewBox.baseVal) {
        naturalWidth = Math.max(naturalWidth, target.viewBox.baseVal.width || 0)
      } else if (target.tagName.toLowerCase() === 'img') {
        naturalWidth = Math.max(naturalWidth, target.naturalWidth || 0)
      }

      if (naturalWidth > 0) {
        clone.style.width = `${Math.min(naturalWidth, 4096)}px`
      }

      this.diagramTrigger = target
      this.diagramViewer.type = isArticleImage ? '图片' : '图表'
      this.diagramViewer.title = isArticleImage
        ? (target.alt || '文章图片')
        : (title ? title.textContent.trim() : '图表')
      this.diagramViewer.open = true
      this.diagramViewer.scale = 1
      this.diagramViewer.fitScale = 1
      this.diagramViewer.offsetX = 0
      this.diagramViewer.offsetY = 0
      this.previousBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      this.$nextTick(() => {
        if (!this.$refs.diagramCanvas) return
        if (clone.tagName.toLowerCase() === 'img' && !clone.complete) {
          clone.addEventListener('load', () => {
            this.$nextTick(() => this.fitDiagramToViewport())
          }, { once: true })
        }
        this.$refs.diagramCanvas.appendChild(clone)
        this.$nextTick(() => {
          this.fitDiagramToViewport()
          if (this.$refs.diagramCloseButton) this.$refs.diagramCloseButton.focus()
        })
      })
    },
    fitDiagramToViewport() {
      const viewport = this.$refs.diagramViewport
      const canvas = this.$refs.diagramCanvas
      const asset = canvas && canvas.firstElementChild
      if (!viewport || !asset) return

      this.diagramViewer.scale = 1
      this.diagramViewer.offsetX = 0
      this.diagramViewer.offsetY = 0

      this.$nextTick(() => {
        if (!this.$refs.diagramViewport || !this.$refs.diagramCanvas) return
        const viewportRect = this.$refs.diagramViewport.getBoundingClientRect()
        const assetRect = asset.getBoundingClientRect()
        if (!assetRect.width || !assetRect.height) return

        const horizontalSpace = Math.max(viewportRect.width - 48, 1)
        const verticalSpace = Math.max(viewportRect.height - 48, 1)
        const fitScale = Math.min(1, horizontalSpace / assetRect.width, verticalSpace / assetRect.height)
        this.diagramViewer.fitScale = Math.max(0.25, Number(fitScale.toFixed(2)))
        this.diagramViewer.scale = this.diagramViewer.fitScale
      })
    },
    resetDiagramView() {
      this.diagramViewer.offsetX = 0
      this.diagramViewer.offsetY = 0
      this.diagramViewer.scale = this.diagramViewer.fitScale
    },
    changeDiagramZoom(amount) {
      const nextScale = Math.min(4, Math.max(0.25, this.diagramViewer.scale + amount))
      this.diagramViewer.scale = Number(nextScale.toFixed(2))
    },
    handleDiagramWheel(event) {
      this.changeDiagramZoom(event.deltaY < 0 ? 0.15 : -0.15)
    },
    startDiagramPan(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return
      this.diagramViewer.dragging = true
      this.diagramViewer.pointerId = event.pointerId
      this.diagramViewer.startX = event.clientX
      this.diagramViewer.startY = event.clientY
      this.diagramViewer.startOffsetX = this.diagramViewer.offsetX
      this.diagramViewer.startOffsetY = this.diagramViewer.offsetY
      if (event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId)
      }
    },
    moveDiagramPan(event) {
      if (!this.diagramViewer.dragging || event.pointerId !== this.diagramViewer.pointerId) return
      this.diagramViewer.offsetX = this.diagramViewer.startOffsetX + event.clientX - this.diagramViewer.startX
      this.diagramViewer.offsetY = this.diagramViewer.startOffsetY + event.clientY - this.diagramViewer.startY
    },
    endDiagramPan(event) {
      if (event.pointerId !== this.diagramViewer.pointerId) return
      this.diagramViewer.dragging = false
      this.diagramViewer.pointerId = null
      if (event.currentTarget.releasePointerCapture && event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    },
    closeDiagramViewer(restoreFocus = true) {
      if (!this.diagramViewer.open) return

      this.diagramViewer.open = false
      this.diagramViewer.dragging = false
      this.diagramViewer.pointerId = null
      document.body.style.overflow = this.previousBodyOverflow

      const trigger = this.diagramTrigger
      this.diagramTrigger = null
      if (restoreFocus && trigger && document.contains(trigger)) {
        this.$nextTick(() => trigger.focus())
      }
    },
    copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text)
      }

      return new Promise((resolve, reject) => {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        const copied = document.execCommand('copy')
        textarea.remove()
        copied ? resolve() : reject(new Error('浏览器不支持复制'))
      })
    },
    scrollToHeading(id) {
      const content = this.$refs.markdownContent
      const element = document.getElementById(id)
      if (!content || !element || !content.contains(element)) return

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
      this.activeHeadingId = id
      if (window.innerWidth < 1536) this.showToc = false
    },
    toggleToc() {
      this.showToc = !this.showToc
      this.$nextTick(() => {
        const focusTarget = this.showToc ? this.$refs.tocClose : this.$refs.tocToggleBtn
        if (focusTarget) focusTarget.focus()
      })
    },
    getReadingPosition() {
      const content = this.$refs.markdownContent
      if (!content) return 0

      const contentRect = content.getBoundingClientRect()
      const scrollableHeight = Math.max(content.scrollHeight - window.innerHeight, 1)
      return Math.min(Math.max(-contentRect.top / scrollableHeight, 0), 1)
    },
    restoreReadingPosition(position) {
      const content = this.$refs.markdownContent
      if (!content) return

      const contentRect = content.getBoundingClientRect()
      const contentTop = window.pageYOffset + contentRect.top
      const scrollableHeight = Math.max(content.scrollHeight - window.innerHeight, 0)
      window.scrollTo(0, Math.max(0, contentTop + scrollableHeight * position))
    },
    async toggleReadingMode() {
      const enteringReadingMode = !this.isReadingMode
      const content = this.$refs.markdownContent
      const hasPendingMermaid = enteringReadingMode && content &&
        Array.from(content.querySelectorAll('.mermaid')).some(node => !node.querySelector('svg'))

      if (hasPendingMermaid) {
        const rendered = await this.renderMermaidDiagrams({ notifyOnError: true })
        if (!rendered) return
      }

      const readingPosition = this.getReadingPosition()
      this.isReadingMode = !this.isReadingMode
      if (this.isReadingMode) this.showToc = false
      document.body.classList.toggle('article-reading-mode', this.isReadingMode)

      await this.$nextTick()

      const mermaidResetByLayout = this.isReadingMode && content &&
        Array.from(content.querySelectorAll('.mermaid')).some(node => !node.querySelector('svg'))
      if (mermaidResetByLayout) {
        const rendered = await this.renderMermaidDiagrams({ notifyOnError: true })
        if (!rendered) {
          this.isReadingMode = false
          document.body.classList.remove('article-reading-mode')
          return
        }
      }

      window.requestAnimationFrame(() => {
        this.restoreReadingPosition(readingPosition)
        this.updateReadingProgress()
      })
    },
    updateReadingProgress() {
      this.readingProgress = this.isReadingMode
        ? Math.round(this.getReadingPosition() * 1000) / 10
        : 0
    },
    handleReadingScroll() {
      if (!this.isReadingMode || this.readingScrollFrame) return

      this.readingScrollFrame = window.requestAnimationFrame(() => {
        this.readingScrollFrame = null
        this.updateReadingProgress()
      })
    },
    handleViewportChange() {
      if (this.diagramViewer.open) {
        this.$nextTick(() => this.fitDiagramToViewport())
      }
      this.handleReadingScroll()
      if (window.innerWidth < 1536 && this.showToc) {
        this.showToc = false
      }
    },
    handleGlobalKeydown(event) {
      if (this.diagramViewer.open) {
        if (event.key === 'Escape') {
          event.preventDefault()
          this.closeDiagramViewer()
        } else if (event.key === '+' || event.key === '=') {
          event.preventDefault()
          this.changeDiagramZoom(0.25)
        } else if (event.key === '-' || event.key === '_') {
          event.preventDefault()
          this.changeDiagramZoom(-0.25)
        } else if (event.key === '0') {
          event.preventDefault()
          this.resetDiagramView()
        } else if (event.key === 'Tab') {
          const viewer = this.$refs.diagramViewer
          const buttons = viewer ? Array.from(viewer.querySelectorAll('button:not(:disabled)')) : []
          if (buttons.length) {
            const first = buttons[0]
            const last = buttons[buttons.length - 1]
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault()
              last.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault()
              first.focus()
            }
          }
        }
        return
      }

      if (event.key === 'Escape' && this.isReadingMode) {
        event.preventDefault()
        this.toggleReadingMode()
        return
      }

      if (event.key === 'Escape' && this.showToc) {
        this.showToc = false
        this.$nextTick(() => {
          if (this.$refs.tocToggleBtn) this.$refs.tocToggleBtn.focus()
        })
      }
    }
  },
  created() {
    this.fetchArticle()
    this.checkLoginStatus()
  },
  mounted() {
    this.showToc = window.innerWidth >= 1536
    window.addEventListener('resize', this.handleViewportChange)
    window.addEventListener('scroll', this.handleReadingScroll, { passive: true })
    document.addEventListener('keydown', this.handleGlobalKeydown)
  },
  beforeDestroy() {
    this.closeDiagramViewer(false)
    this.disconnectHeadingObserver()
    window.removeEventListener('resize', this.handleViewportChange)
    window.removeEventListener('scroll', this.handleReadingScroll)
    document.removeEventListener('keydown', this.handleGlobalKeydown)
    if (this.readingScrollFrame) window.cancelAnimationFrame(this.readingScrollFrame)
    document.body.classList.remove('article-reading-mode')
  },
  watch: {
    '$route.params.id'(newId, oldId) {
      if (newId !== oldId) this.fetchArticle()
    }
  }
}
</script>

<style>
.article-detail {
  --toc-width: 240px;
  --wide-reading-width: 1200px;
  --wide-layout-gap: 48px;
  --prose-reading-width: 100%;
  width: min(calc(100% - 2rem), var(--reading-width));
  margin: 0 auto;
  padding: clamp(1.5rem, 5vw, 4rem) 0;
  position: relative;
}

.article-error {
  padding: clamp(2rem, 8vw, 4rem) 1.5rem;
  text-align: center;
  background: var(--card-bg);
  border-radius: 12px;
}

.article-error h1 {
  margin-top: 0;
  font-size: clamp(1.75rem, 5vw, 2.5rem);
}

.article-error p {
  margin-bottom: 1.5rem;
  color: var(--text-secondary);
}

.reading-mode-toggle {
  grid-column: 2;
  justify-self: end;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: max-content;
  min-height: 40px;
  margin: 0 0 1rem auto;
  padding: 0.55rem 0.85rem;
  border: 1px solid rgba(107, 95, 76, 0.28);
  border-radius: 999px;
  background: #faf9f8;
  color: #4a4033;
  font: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
}

.reading-mode-toggle:hover {
  color: var(--primary-color);
  background: #fff;
  border-color: rgba(67, 97, 238, 0.45);
}

.reading-mode-toggle:focus-visible {
  outline: 3px solid rgba(67, 97, 238, 0.42);
  outline-offset: 3px;
}

.reading-mode-toggle:disabled {
  border-color: #d8dee7;
  background: #f1f4f7;
  color: #7b8797;
  cursor: wait;
}

.reading-mode-toggle--active {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: var(--z-reading-controls);
  margin: 0;
  border-color: transparent;
  background: #2d3748;
  color: #fff;
}

.reading-mode-toggle--active:hover {
  color: #fff;
  background: var(--primary-color);
  border-color: transparent;
}

.reading-mode-shortcut {
  padding: 0.1rem 0.35rem;
  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 4px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 500;
}

.reading-progress {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-reading-controls);
  height: 3px;
  background: rgba(67, 97, 238, 0.12);
}

.reading-progress span {
  display: block;
  width: 100%;
  height: 100%;
  background: var(--primary-color);
  transform-origin: left center;
  transition: transform 0.12s linear;
}

.article-comments {
  grid-column: 2;
}

body.article-reading-mode,
body.article-reading-mode #app {
  background: #faf9f8;
}

body.article-reading-mode .navbar {
  display: none;
}

body.article-reading-mode .content-wrapper {
  padding: 0;
}

body.article-reading-mode .main-content {
  width: 100%;
}

.article-detail.article-detail--reading {
  display: block;
  width: 100%;
  max-width: none;
  padding: 0;
}

.article-detail--reading .article-header,
.article-detail--reading .toc-backdrop,
.article-detail--reading .toc-container,
.article-detail--reading .toc-toggle-btn,
.article-detail--reading .article-footer,
.article-detail--reading .article-comments,
.article-detail--reading .float-button {
  display: none;
}

.article-detail--reading .markdown-body {
  width: min(calc(100% - 3rem), 1320px);
  min-height: 100vh;
  margin: 0 auto;
  padding: clamp(4.75rem, 6vw, 6.5rem) clamp(1.5rem, 6vw, 5rem) 5rem;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

/* Markdown reading surface */
.markdown-body {
  font-family: inherit;
  -webkit-font-smoothing: antialiased;
  background-color: #faf9f8;  /* 更柔和的米白色背景 */
  padding: clamp(1.5rem, 4vw, 4rem);
  border-radius: 12px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(107, 95, 76, 0.1);  /* 暖棕色边框，配合整体风格 */
  font-size: 1.1rem;
  line-height: 1.8;
  color: #2c3e50;  /* 更舒适的阅读文字颜色 */
}

.markdown-body > h1,
.markdown-body > h2,
.markdown-body > h3,
.markdown-body > h4,
.markdown-body > h5,
.markdown-body > h6,
.markdown-body > p,
.markdown-body > ul,
.markdown-body > ol,
.markdown-body > blockquote,
.markdown-body > hr {
  width: min(100%, var(--prose-reading-width));
  margin-left: auto;
  margin-right: auto;
}

.markdown-body > pre,
.markdown-body > .code-block {
  width: fit-content;
  min-width: min(100%, var(--prose-reading-width));
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.markdown-body > table,
.markdown-body > .table-wrapper,
.markdown-body > .diagram-block {
  width: 100%;
}

.markdown-body > img {
  width: auto;
}

/* Table styles */
.markdown-body .table-wrapper {
  max-width: 100%;
  margin: 1.5rem 0;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  -webkit-overflow-scrolling: touch;
}

.markdown-body .table-wrapper:focus-visible {
  outline: 3px solid rgba(67, 97, 238, 0.2);
  outline-offset: 2px;
}

.markdown-body table {
  width: 100%;
  min-width: 560px;
  margin: 0;
  border-collapse: collapse;
  border-spacing: 0;
}

.markdown-body table tr {
  background-color: #fff;
  border-top: 1px solid #eaecef;
}

.markdown-body table tr:nth-child(2n) {
  background-color: #f8f9fa;
}

.markdown-body table th,
.markdown-body table td {
  padding: 0.75rem 1rem;
  border: 1px solid #eaecef;
  text-align: left;
}

.markdown-body table th {
  font-weight: 600;
  background-color: #f8f9fa;
}

/* Code block styles */
.markdown-body .code-block,
.markdown-body .diagram-block {
  margin: 1.75rem 0;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  overflow: hidden;
  background: #fff;
}

.markdown-body .code-toolbar,
.markdown-body .diagram-toolbar {
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.4rem 0.65rem 0.4rem 0.85rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f6f8fa;
  color: #64748b;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.78rem;
}

.markdown-body .code-copy {
  min-width: 58px;
  min-height: 32px;
  padding: 0.3rem 0.65rem;
  border: 1px solid #d5dce5;
  border-radius: 6px;
  background: #fff;
  color: #475569;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease;
}

.markdown-body .code-copy:hover,
.markdown-body .code-copy:focus-visible {
  border-color: var(--primary-color);
  color: var(--primary-color);
  outline: none;
}

.markdown-body .code-copy.is-copied {
  border-color: #2f855a;
  background: #f0fff4;
  color: #276749;
}

.markdown-body .code-block pre {
  margin: 0;
  border: 0;
  border-radius: 0;
}

.markdown-body pre {
  background-color: #ffffff;  /* 代码块使用白色背景形成对比 */
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  max-width: 100%;
  box-sizing: border-box;
  border: 1px solid #eaecef;
}

.markdown-body pre > code {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre;
  word-wrap: normal;
  display: block;
  padding: 0;
  background: none;
  border-radius: 0;
  overflow-x: auto;
  max-width: 100%;
}

/* Inline code styles */
.markdown-body code {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  font-size: 0.9em;
  background-color: rgba(0, 0, 0, 0.05);
  padding: 0.2em 0.4em;
  border-radius: 3px;
  margin: 0 0.2em;
}

.markdown-body .task-list-item {
  list-style: none;
}

.markdown-body .task-list-item-checkbox {
  width: 1rem;
  height: 1rem;
  margin: 0 0.45rem 0 -1.4rem;
  accent-color: var(--primary-color);
  vertical-align: -0.15em;
}

.markdown-body .footnotes {
  width: min(100%, var(--prose-reading-width));
  margin: 3rem auto 0;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
  color: var(--text-secondary);
  font-size: 0.92rem;
}

.markdown-body .katex-display {
  max-width: 100%;
  margin: 1.6rem 0;
  padding: 0.35rem 0;
  overflow-x: auto;
  overflow-y: hidden;
}

.markdown-body .diagram-block .mermaid {
  position: relative;
  display: flex;
  justify-content: center;
  min-width: 0;
  margin: 0;
  padding: 1.5rem;
  border: 0;
  border-radius: 0;
  overflow-x: auto;
  background: #fff;
  white-space: pre;
}

.markdown-body .diagram-block .mermaid.is-rendering {
  min-height: 180px;
  color: transparent;
  overflow: hidden;
}

.markdown-body .diagram-block .mermaid.is-rendering::after {
  content: '正在生成图表…';
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: #64748b;
  font-family: inherit;
  font-size: 0.9rem;
  white-space: normal;
}

.markdown-body .diagram-block .mermaid svg {
  max-width: 100%;
  height: auto;
}

.markdown-body .diagram-preview-target {
  cursor: zoom-in;
  transition: filter 0.2s ease, opacity 0.2s ease;
}

.markdown-body .diagram-preview-target:hover {
  filter: contrast(1.04);
}

.markdown-body .diagram-preview-target:focus-visible {
  outline: 3px solid rgba(67, 97, 238, 0.35);
  outline-offset: 5px;
}

.markdown-body .diagram-zoom-hint {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 0.65rem;
  border: 1px solid #d5dce5;
  border-radius: 999px;
  background: #fff;
  color: #475569;
  font-family: inherit;
  font-size: 0.74rem;
  font-weight: 600;
}

.markdown-body .diagram-block .mermaid.is-render-error {
  justify-content: flex-start;
  color: #9b2c2c;
  background: #fff5f5;
}

.markdown-body .plantuml-diagram {
  min-height: 160px;
  padding: 1.5rem;
  text-align: center;
}

.markdown-body .plantuml-image {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto 1rem;
  border-radius: 0;
  box-shadow: none;
}

.markdown-body .plantuml-status {
  margin: 2.5rem 0;
  color: #64748b;
}

.markdown-body .plantuml-diagram.is-render-error .plantuml-status {
  color: #9b2c2c;
}

.markdown-body .plantuml-source {
  margin-top: 0.75rem;
  text-align: left;
}

.markdown-body .plantuml-source summary {
  width: fit-content;
  min-height: 32px;
  color: #64748b;
  cursor: pointer;
}

.markdown-body .plantuml-source summary:hover,
.markdown-body .plantuml-source summary:focus-visible {
  color: var(--primary-color);
  outline: none;
}

.markdown-body .plantuml-source pre {
  margin: 0.75rem 0 0;
  text-align: left;
}

/* Diagram fullscreen viewer */
.diagram-viewer {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: clamp(0.75rem, 2vw, 1.5rem);
  background: rgba(17, 24, 39, 0.78);
  backdrop-filter: blur(8px);
  z-index: 9999;
}

.diagram-viewer__panel {
  width: min(100%, 1800px);
  height: min(100%, 1100px);
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 18px;
  background: #f8fafc;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.32);
}

.diagram-viewer__header {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem 0.75rem 1.25rem;
  border-bottom: 1px solid #dfe5ec;
  background: rgba(255, 255, 255, 0.96);
}

.diagram-viewer__eyebrow {
  display: block;
  margin-bottom: 0.15rem;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.diagram-viewer__title {
  margin: 0;
  color: #1e293b;
  font-size: clamp(1rem, 2vw, 1.25rem);
  line-height: 1.2;
}

.diagram-viewer__controls {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.diagram-viewer__button {
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border: 1px solid #d5dce5;
  border-radius: 9px;
  background: #fff;
  color: #334155;
  cursor: pointer;
  font: inherit;
  transition: border-color 0.16s ease, background-color 0.16s ease, color 0.16s ease;
}

.diagram-viewer__button:hover,
.diagram-viewer__button:focus-visible {
  border-color: var(--primary-color);
  background: #f7f4ee;
  color: var(--primary-color);
  outline: none;
}

.diagram-viewer__button:disabled {
  cursor: not-allowed;
  opacity: 0.42;
}

.diagram-viewer__scale {
  min-width: 68px;
  font-variant-numeric: tabular-nums;
  font-size: 0.84rem;
  font-weight: 700;
}

.diagram-viewer__close {
  margin-left: 0.35rem;
  border-color: transparent;
  background: #e9edf2;
}

.diagram-viewer__viewport {
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background-color: #f8fafc;
  background-image:
    linear-gradient(rgba(148, 163, 184, 0.13) 1px, transparent 1px),
    linear-gradient(90deg, rgba(148, 163, 184, 0.13) 1px, transparent 1px);
  background-size: 24px 24px;
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.diagram-viewer__viewport.is-dragging {
  cursor: grabbing;
}

.diagram-viewer__canvas {
  position: absolute;
  top: 50%;
  left: 50%;
  transform-origin: center;
  will-change: transform;
}

.diagram-viewer__canvas .diagram-viewer__asset {
  display: block;
  max-width: none !important;
  max-height: none !important;
  height: auto;
  margin: 0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
  pointer-events: none;
}

.diagram-viewer__hint {
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0.5rem 1rem calc(0.5rem + env(safe-area-inset-bottom));
  border-top: 1px solid #dfe5ec;
  background: #fff;
  color: #64748b;
  font-size: 0.78rem;
  letter-spacing: 0.02em;
}

.diagram-viewer-enter-active,
.diagram-viewer-leave-active {
  transition: opacity 0.18s ease;
}

.diagram-viewer-enter-active .diagram-viewer__panel,
.diagram-viewer-leave-active .diagram-viewer__panel {
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.diagram-viewer-enter,
.diagram-viewer-leave-to {
  opacity: 0;
}

.diagram-viewer-enter .diagram-viewer__panel,
.diagram-viewer-leave-to .diagram-viewer__panel {
  opacity: 0;
  transform: scale(0.985);
}

/* Headers */
.markdown-body h1,
.markdown-body h2,
.markdown-body h3,
.markdown-body h4,
.markdown-body h5,
.markdown-body h6 {
  font-weight: 600;
  line-height: 1.25;
  scroll-margin-top: calc(var(--header-height) + 1rem);
}

.markdown-body h1,
.markdown-body h2 {
  margin-top: 4rem;
  margin-bottom: 1rem;
}

.markdown-body h3,
.markdown-body h4,
.markdown-body h5,
.markdown-body h6 {
  margin-top: 3rem;
  margin-bottom: 0.75rem;
}

.markdown-body h1 {
  font-size: 2em;
  padding-bottom: 0.3em;
  border-bottom: 1px solid #eaecef;
}

.markdown-body h2 {
  font-size: 1.5em;
  padding-bottom: 0.3em;
  border-bottom: 1px solid #eaecef;
}

.markdown-body h3 {
  font-size: 1.25em;
}

.article-header {
  text-align: center;
  margin-bottom: 3rem;
}

.article-meta {
  margin-bottom: 1.5rem;
}

.meta-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.article-category {
  color: var(--primary-color);
  font-weight: 500;
  padding: 0.5rem 1rem;
  background: rgba(107, 95, 76, 0.1);
  border-radius: 20px;
  font-size: 0.9rem;
}

.date-info {
  display: flex;
  align-items: center;
}

.article-update-date {
  color: var(--text-color);
  opacity: 0.7;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.article-update-date i {
  font-size: 0.8rem;
}

.article-title {
  font-size: 3rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  margin: 0 0 1rem;
  line-height: 1.2;
  text-wrap: balance;
}

.article-desc {
  font-size: 1.25rem;
  color: var(--text-color);
  opacity: 0.8;
  line-height: 1.6;
  margin: 0;
  text-wrap: pretty;
}

.article-content {
  margin-bottom: 3rem;
  overflow: visible;
}

.article-footer {
  border-top: 1px solid var(--border-color);
  padding-top: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.article-tags {
  display: flex;
  gap: 0.5rem;
}

.tag {
  padding: 0.25rem 0.75rem;
  background: var(--secondary-bg);
  border-radius: 9999px;
  font-size: 0.875rem;
  color: var(--text-color);
}

.action-buttons {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.action-button {
  font-size: 0.9rem;
  padding: 0;
  color: #6b5f4c;  /* 暖棕色，与分类标签颜色一致 */
  transition: all 0.2s ease;
}

.action-button:hover {
  color: var(--primary-color);
  transform: translateY(-1px);
}

.delete-button:hover {
  color: #dc3545;  /* 删除按钮悬浮时显示红色 */
}

.float-button {
  position: fixed;
  right: 2rem;
  bottom: 2rem;
  padding: 0.75rem 1.5rem;
  border-radius: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s, box-shadow 0.2s;
  z-index: var(--z-floating);
}

.float-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

/* Image styles */
.markdown-body img {
  max-width: 100%;  /* 确保图片不会超出容器宽度 */
  height: auto;     /* 保持图片比例 */
  display: block;   /* 块级显示，避免行内布局问题 */
  margin: 1.5rem auto;  /* 上下间距和水平居中 */
  border-radius: 8px;   /* 圆角 */
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);  /* 轻微阴影 */
  background-color: #fff;  /* 白色背景 */
  box-sizing: border-box;  /* 包含内边距在宽度内 */
}

/* 大图片的特殊处理 */
.markdown-body img[src*="full-width"] {
  width: 100%;
  margin-left: 0;
  margin-right: 0;
}

/* 目录样式 */
.toc-backdrop {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(24, 22, 18, 0.42);
  z-index: var(--z-overlay-backdrop);
}

.toc-container {
  position: fixed;
  top: 120px;
  right: 1.5rem;
  width: var(--toc-width);
  max-height: calc(100vh - 160px);
  padding: 1rem;
  overflow: hidden;
  background-color: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(107, 95, 76, 0.14);
  z-index: var(--z-overlay);
}

.toc-toggle-btn {
  position: fixed;
  top: 120px;
  right: 1.5rem;
  width: 50px;
  height: 50px;
  padding: 0;
  background-color: var(--card-bg);
  border: none;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(107, 95, 76, 0.16);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: var(--z-floating);
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.toc-toggle-btn:hover,
.toc-toggle-btn:focus-visible {
  transform: translateY(-2px);
  background-color: #eee7dc;
  outline: none;
}

.toc-toggle-btn i {
  font-size: 1.1rem;
  color: var(--text-color);
  margin-bottom: 0.25rem;
}

.toggle-text {
  font-size: 0.7rem;
  color: var(--text-secondary);
  font-weight: 500;
}

.toc-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color);
}

.toc-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.toc-toggle {
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
}

.toc-toggle:hover,
.toc-toggle:focus-visible {
  color: var(--primary-color);
  background-color: rgba(107, 95, 76, 0.1);
  outline: none;
}

.toc-content {
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.toc-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.toc-item {
  margin: 0.25rem 0;
}

.toc-link {
  width: 100%;
  min-height: 44px;
  padding: 0.5rem;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  text-align: left;
  cursor: pointer;
}

.toc-link:hover,
.toc-link:focus-visible,
.toc-link.is-active {
  color: var(--primary-color);
  background-color: rgba(107, 95, 76, 0.1);
  outline: none;
}

.toc-link.is-active {
  box-shadow: inset 3px 0 0 var(--primary-color);
}

.toc-level-1 .toc-link {
  color: var(--text-color);
  font-size: 1rem;
  font-weight: 600;
}

.toc-level-2 .toc-link {
  padding-left: 1rem;
  font-size: 0.95rem;
}

.toc-level-3 .toc-link,
.toc-level-4 .toc-link,
.toc-level-5 .toc-link,
.toc-level-6 .toc-link {
  padding-left: 1.5rem;
  font-size: 0.875rem;
}

.toc-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (min-width: 1536px) {
  .article-detail {
    width: min(
      calc(100% - 2rem),
      calc((var(--toc-width) * 2) + var(--wide-reading-width) + (var(--wide-layout-gap) * 2))
    );
    display: grid;
    grid-template-columns: var(--toc-width) minmax(0, 1fr) var(--toc-width);
    column-gap: var(--wide-layout-gap);
    align-items: start;
  }

  .article-header,
  .article-content,
  .article-footer,
  .article-detail > .comment-section,
  .article-detail > .article-error,
  .article-detail > .loading-container {
    grid-column: 2;
  }

  .toc-container {
    position: sticky;
    top: calc(var(--header-height) + 1.5rem);
    right: auto;
    width: 100%;
    grid-column: 3;
    grid-row: 1 / span 5;
    align-self: start;
    padding: 1rem 0;
    background: transparent;
    border-radius: 0;
    box-shadow: none;
  }

  .toc-content {
    max-height: calc(100vh - var(--header-height) - 7rem);
  }
}

@media (max-width: 1535px) {
  .toc-backdrop {
    display: block;
  }

  .toc-container {
    top: 50%;
    left: 50%;
    right: auto;
    width: min(calc(100% - 2rem), 420px);
    max-height: min(70vh, 620px);
    transform: translate(-50%, -50%);
  }

  .toc-content {
    max-height: calc(min(70vh, 620px) - 96px);
  }
}

@media (max-width: 768px) {
  .article-detail {
    width: min(calc(100% - 1.5rem), var(--reading-width));
    padding: 1.5rem 0;
  }

  .markdown-body {
    padding: 1.5rem;
    font-size: 1rem;
  }

  .article-header {
    margin-bottom: 2rem;
  }

  .meta-row {
    flex-direction: column;
    gap: 1rem;
  }

  .article-title {
    font-size: 2rem;
  }

  .article-desc {
    font-size: 1.1rem;
  }

  .float-button {
    right: 1rem;
    bottom: calc(5rem + env(safe-area-inset-bottom));
    padding: 0.5rem 1rem;
  }
  .toc-container {
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-height: min(70vh, 560px);
    padding: 1rem;
    border-radius: 16px 16px 0 0;
    transform: none;
  }

  .toc-toggle-btn {
    top: auto;
    right: 1rem;
    bottom: calc(1rem + env(safe-area-inset-bottom));
    width: 48px;
    height: 48px;
  }

  .toc-toggle-btn i {
    font-size: 1rem;
  }

  .toggle-text {
    font-size: 0.7rem;
  }

  .article-content img {
    max-width: calc(100% - 1rem);
    margin: 1rem auto;
  }

  .markdown-body .diagram-block .mermaid,
  .markdown-body .plantuml-diagram {
    padding: 1rem;
  }

  .markdown-body .diagram-zoom-hint {
    min-height: 26px;
    padding: 0 0.5rem;
    font-size: 0.7rem;
  }

  .diagram-viewer {
    padding: 0;
  }

  .diagram-viewer__panel {
    width: 100%;
    height: 100%;
    border: 0;
    border-radius: 0;
  }

  .diagram-viewer__header {
    min-height: 64px;
    padding: calc(0.5rem + env(safe-area-inset-top)) 0.65rem 0.5rem 0.9rem;
  }

  .diagram-viewer__eyebrow,
  .diagram-viewer__hint {
    display: none;
  }

  .diagram-viewer__controls {
    gap: 0.25rem;
  }

  .diagram-viewer__button {
    min-width: 42px;
    min-height: 42px;
  }

  .diagram-viewer__scale {
    min-width: 58px;
    padding-inline: 0.35rem;
    font-size: 0.78rem;
  }

  .diagram-viewer__close {
    margin-left: 0;
  }

  .toc-header {
    padding-bottom: 0.75rem;
  }

  .toc-title {
    font-size: 1rem;
    gap: 0.5rem;
  }

  .toc-toggle {
    font-size: 0.8rem;
  }

  .toc-content {
    max-height: calc(min(70vh, 560px) - 96px);
  }

  .toc-list {
    display: block;
  }

  .toc-link {
    min-height: 44px;
  }
}

@media (max-width: 768px) {
  .reading-mode-toggle--active {
    top: 0.75rem;
    right: 0.75rem;
  }

  .reading-mode-shortcut {
    display: none;
  }

  .article-detail--reading .markdown-body {
    width: 100%;
    padding: 4.5rem 1.25rem 3rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .reading-mode-toggle,
  .reading-progress span,
  .diagram-viewer-enter-active,
  .diagram-viewer-leave-active,
  .diagram-viewer-enter-active .diagram-viewer__panel,
  .diagram-viewer-leave-active .diagram-viewer__panel {
    transition: none;
  }
}
</style>
